import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dashboardKeys } from "@/features/dashboard/hooks";
import { employeeKeys } from "@/features/employees/api";
import {
  separationApi,
  separationKeys,
  type DecisionInput,
  type ExitAnswerInput,
  type ExitQuestionInput,
  type Separation,
  type TerminateInput,
} from "@/features/separation/api";
import type { QueryParams } from "@/lib/api/client";

export function useNoticeTerms() {
  return useQuery({
    queryKey: separationKeys.noticeTerms(),
    queryFn: separationApi.noticeTerms,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMySeparation() {
  return useQuery({ queryKey: separationKeys.mine(), queryFn: separationApi.mine });
}

/** Both phases, so the employee's joining forms and leaving steps arrive together. */
export function useMyTasks() {
  return useQuery({ queryKey: separationKeys.myTasks(), queryFn: separationApi.myTasks });
}

export function useExitInterviewForm() {
  return useQuery({ queryKey: separationKeys.exitForm(), queryFn: separationApi.exitForm });
}

export function useSeparations(status?: string) {
  return useQuery({
    queryKey: separationKeys.list(status),
    queryFn: () => separationApi.list(status),
  });
}

export function useEmployeeTasks(userId: string | undefined) {
  return useQuery({
    queryKey: separationKeys.employeeTasks(userId ?? ""),
    queryFn: () => separationApi.employeeTasks(userId!),
    enabled: Boolean(userId),
  });
}

/** The forms every joiner signs now. */
export function useJoiningForms() {
  return useQuery({
    queryKey: separationKeys.joiningForms(),
    queryFn: separationApi.joiningForms,
  });
}

/**
 * Send the current joining forms, to one person or to everybody, taking back
 * the old ones still unsigned. Every list of tasks and documents changes.
 */
export function useSendJoiningForms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string | null) =>
      userId ? separationApi.sendJoiningForms(userId) : separationApi.sendJoiningFormsToEveryone(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: separationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** Keep new wording for a form; a change is a new version of it. */
export function useSaveJoiningForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, text }: { key: string; text: string }) =>
      separationApi.saveJoiningForm(key, text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: separationKeys.joiningForms() });
    },
  });
}

/** Every active member of staff, and where they stand with one form. */
export function useJoiningFormPeople(key: string | null) {
  return useQuery({
    queryKey: separationKeys.joiningFormPeople(key ?? ""),
    queryFn: () => separationApi.joiningFormPeople(key!),
    enabled: Boolean(key),
  });
}

/** Send the current version of one form to the people chosen, or everybody. */
export function useSendJoiningForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      choice,
    }: {
      key: string;
      choice: { userIds: string[] } | { everyone: true };
    }) => separationApi.sendJoiningForm(key, choice),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: separationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

/** Everything still waiting on somebody, across all employees. */
export function useOpenTasks(filters: QueryParams = {}) {
  return useQuery({
    queryKey: separationKeys.openTasks(filters),
    queryFn: () => separationApi.openTasks(filters),
  });
}

export function useExitAnswers(separationId: string | null | undefined) {
  return useQuery({
    queryKey: separationKeys.exitAnswers(separationId ?? ""),
    queryFn: () => separationApi.exitAnswers(separationId!),
    enabled: Boolean(separationId),
  });
}

export function useExitQuestions() {
  return useQuery({ queryKey: separationKeys.questions(), queryFn: separationApi.questions });
}

/**
 * Every write here moves more than the separation itself.
 *
 * Approving a resignation changes the employment status on the record, and
 * ticking off a task changes what the dashboard says is outstanding, so both
 * of those caches go with it.
 */
function useSeparationMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: separationKeys.all });
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useResign() {
  return useSeparationMutation((reason?: string) => separationApi.resign(reason));
}

export function useWithdrawResignation() {
  return useSeparationMutation<void, Separation>(() => separationApi.withdraw());
}

export function useSubmitExitInterview() {
  return useSeparationMutation((answers: ExitAnswerInput[]) =>
    separationApi.submitExitInterview(answers),
  );
}

export function useTerminate(userId: string) {
  return useSeparationMutation((payload: TerminateInput) =>
    separationApi.terminate(userId, payload),
  );
}

export function useApproveSeparation() {
  return useSeparationMutation(({ id, ...payload }: DecisionInput & { id: string }) =>
    separationApi.approve(id, payload),
  );
}

export function useRejectSeparation() {
  return useSeparationMutation(({ id, note }: { id: string; note: string }) =>
    separationApi.reject(id, note),
  );
}

export function useCompleteTask() {
  return useSeparationMutation(({ taskId, note }: { taskId: string; note?: string }) =>
    separationApi.completeTask(taskId, note),
  );
}

export function useUploadForTask() {
  return useSeparationMutation(
    ({ taskId, file, note }: { taskId: string; file: File; note?: string }) =>
      separationApi.uploadForTask(taskId, file, note),
  );
}

export function useReopenTask() {
  return useSeparationMutation((taskId: string) => separationApi.reopenTask(taskId));
}

export function useSetExitQuestions() {
  return useSeparationMutation((questions: ExitQuestionInput[]) =>
    separationApi.setQuestions(questions),
  );
}
